import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { IApplicationState } from '.';
import ContentConstants from '../contentConstants';
import {
	EditState,
	ISummaryItem,
	ISurveyEditModel,
	ISurveyQuestionEditModel,
	ITranslationEditItem,
	IValidationErrorCollection,
	SurveySummaryItemType,
	SurveySummaryItemTypeId,
} from '../models';
import * as surveyApi from '../services/SurveyAPI';
import * as surveyQuestionApi from '../services/SurveyQuestionAPI';
import * as translationsApi from '../services/TranslationsAPI';
import { TranslationHelper } from '../utilities';
import { assertNonNullable } from '../utilities/assertions';

export interface ISurveyQuestionsAdminState {
	isLoadingOrSaving: boolean;
	isError: boolean;
	questions: ISurveyQuestionEditModel[];
	originalQuestions: { [surveyQuestionId: number]: Pick<ISurveyQuestionEditModel, 'isRequired'> };
	validations: { errors: IValidationErrorCollection; warnings: IValidationErrorCollection };
	translations: { [surveyQuestionId: number]: ITranslationEditItem[] };
	originalTranslations: { [surveyQuestionId: number]: { [cultureCode: string]: ITranslationEditItem } };
}

export const initialState: ISurveyQuestionsAdminState = {
	isLoadingOrSaving: true,
	isError: false,
	questions: [],
	originalQuestions: {},
	validations: { errors: {}, warnings: {} },
	translations: {},
	originalTranslations: {},
};

export const loadSurveyQuestionsAdminData = createAsyncThunk(
	'surveyQuestionsAdmin/loadSurveyQuestionsAdminData',
	async (surveyId: number, { getState, rejectWithValue }) => {
		try {
			const { survey } = getState() as IApplicationState;
			const [surveySummaryItems, translations] = await Promise.all([
				surveyApi.getSurveySummaryItems(surveyId, {
					ignoreValidations: true,
					summaryItemTypes: [SurveySummaryItemTypeId.Questions],
				}),
				translationsApi.getAllTranslations(surveyId, 'Questions'),
			]);

			const questions = surveySummaryItems[0].value?.find(
				(questionSummaryItem: ISummaryItem) => questionSummaryItem.propertyName === SurveySummaryItemType.Questions,
			)?.value[SurveySummaryItemType.Questions] as ISurveyQuestionEditModel[];

			return { questions, translations, defaultCultureCode: survey.surveyEditModel.defaultCultureCode };
		} catch {
			return rejectWithValue({ questions: initialState.questions, translations: initialState.translations });
		}
	},
);

export const saveSurveyQuestionAdminData = createAsyncThunk(
	'surveyQuestionsAdmin/saveSurveyQuestionAdminData',
	async (surveyId: number, { getState, rejectWithValue }) => {
		try {
			const { surveyQuestionsAdmin, survey } = getState() as IApplicationState;
			const [questions, translations] = await Promise.all([
				surveyQuestionApi.updateSurveyQuestions(surveyId, surveyQuestionsAdmin.questions),
				translationsApi.saveAllTranslations(surveyId, 'Questions', surveyQuestionsAdmin.translations),
			]);
			return { questions, translations, defaultCultureCode: survey.surveyEditModel.defaultCultureCode };
		} catch {
			return rejectWithValue({ questions: initialState.questions, translations: initialState.translations });
		}
	},
);

const validateTranslations = (
	translations: ITranslationEditItem[],
	defaultCultureCode: string,
): {
	errors: string[];
	warnings: string[];
} =>
	translations.reduce(
		(validationResult: { errors: string[]; warnings: string[] }, translation: ITranslationEditItem) => {
			const isTextValueNotEmpty = /\S+/.test(translation.textValue);

			if (isTextValueNotEmpty) {
				return validationResult;
			}

			if (translation.cultureCode === defaultCultureCode) {
				validationResult.errors.push('Text is required.');
			} else if (validationResult.warnings.indexOf(ContentConstants.WARNING_MISSING_TRANSLATIONS) === -1) {
				validationResult.warnings.push(ContentConstants.WARNING_MISSING_TRANSLATIONS);
			}

			return validationResult;
		},
		{ errors: [], warnings: [] },
	);

const surveyQuestionsAdminSlice = createSlice({
	name: 'surveyQuestionsAdmin',
	initialState: initialState,
	reducers: {
		toggleResponseIsRequired: (state, action: PayloadAction<Pick<ISurveyQuestionEditModel, 'surveyQuestionId' | 'isRequired'>>) => {
			const {
				payload: { surveyQuestionId, isRequired },
			} = action;

			const toggleAllResponseIsRequired = surveyQuestionId === undefined;

			for (const question of state.questions) {
				if (
					!toggleAllResponseIsRequired &&
					(question.surveyQuestionId !== surveyQuestionId ||
						(question.surveyQuestionId === surveyQuestionId && question.isRequired === isRequired))
				) {
					continue;
				}

				assertNonNullable(question.surveyQuestionId);

				question.isRequired = isRequired;
				question.editState =
					question.isRequired === state.originalQuestions[question.surveyQuestionId].isRequired ? EditState.None : EditState.Update;

				if (!toggleAllResponseIsRequired) {
					break;
				}
			}
		},
		updateSurveyQuestionTranslation: (
			state,
			action: PayloadAction<
				Pick<ISurveyQuestionEditModel, 'surveyQuestionId'> &
					Pick<ITranslationEditItem, 'cultureCode' | 'textValue'> &
					Pick<ISurveyEditModel, 'defaultCultureCode'>
			>,
		) => {
			const {
				payload: { surveyQuestionId, cultureCode, defaultCultureCode, textValue },
			} = action;

			assertNonNullable(surveyQuestionId);

			for (const translation of state.translations[surveyQuestionId]) {
				if (translation.cultureCode !== cultureCode) {
					continue;
				}

				translation.textValue = textValue;
				translation.editState = TranslationHelper.getEditState(translation, state.originalTranslations[surveyQuestionId][cultureCode]);

				if (cultureCode === defaultCultureCode) {
					const question = state.questions.find((q) => q.surveyQuestionId === surveyQuestionId);
					assertNonNullable(question);
					question.text = textValue;
				}
				break;
			}

			const { errors, warnings } = validateTranslations(state.translations[surveyQuestionId], defaultCultureCode);
			state.validations.errors[surveyQuestionId] = errors;
			state.validations.warnings[surveyQuestionId] = warnings;
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(isAnyOf(loadSurveyQuestionsAdminData.pending, saveSurveyQuestionAdminData.pending), (state) => {
				state.isLoadingOrSaving = true;
				state.isError = false;
			})
			.addMatcher(isAnyOf(loadSurveyQuestionsAdminData.fulfilled, saveSurveyQuestionAdminData.fulfilled), (state, action) => {
				state.isLoadingOrSaving = false;
				state.questions = action.payload.questions;
				state.originalQuestions = state.questions.reduce(
					(map: { [surveyQuestionId: number]: Pick<ISurveyQuestionEditModel, 'isRequired'> }, value) => {
						assertNonNullable(value.surveyQuestionId);
						const { isRequired } = value;
						map[value.surveyQuestionId] = { isRequired };
						return map;
					},
					{},
				);
				state.translations = action.payload.translations;
				state.originalTranslations = Object.entries(action.payload.translations).reduce(
					(questionTranslationsMap, [surveyQuestionId, translations]) => {
						questionTranslationsMap[surveyQuestionId] = translations.reduce((cultureCodeTranslationsMap, translation) => {
							cultureCodeTranslationsMap[translation.cultureCode] = translation;
							return cultureCodeTranslationsMap;
						}, {});
						return questionTranslationsMap;
					},
					{},
				);
				state.validations = Object.entries(action.payload.translations).reduce(
					(validations, [surveyQuestionId, translations]) => {
						const { errors, warnings } = validateTranslations(translations, action.payload.defaultCultureCode);
						validations.errors[surveyQuestionId] = errors;
						validations.warnings[surveyQuestionId] = warnings;
						return validations;
					},
					{ errors: {}, warnings: {} },
				);
			})
			.addMatcher(isAnyOf(loadSurveyQuestionsAdminData.rejected, saveSurveyQuestionAdminData.rejected), (state) => {
				state.isLoadingOrSaving = false;
				state.isError = true;
			});
	},
});

export const { toggleResponseIsRequired, updateSurveyQuestionTranslation } = surveyQuestionsAdminSlice.actions;
export default surveyQuestionsAdminSlice.reducer;
